import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_feasibility-hub-10/artifacts/nb8rpfwp_Ruam%20Architects%20Logo.jpg";

export default function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Show error from OAuth redirect
  const oauthError = location.state?.error || '';

  // If already logged in, redirect — run in effect to avoid setState-during-render warnings
  useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/', { replace: true });
    }
  }, [user, navigate]);

  if (user) return null;

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-center justify-center px-6 pt-16" data-testid="login-page">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: `url(/projects/hero.jpg)`,
        backgroundSize: 'cover', backgroundPosition: 'center'
      }} />

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-8 transition-colors" data-testid="login-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>

        <div className="glass-card rounded-sm p-8">
          {/* Logo + Title */}
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Raum Architects" className="w-16 h-16 rounded-full object-cover ring-2 ring-brand-accent/40 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-medium text-white mb-2">Welcome</h1>
            <p className="text-white/50 text-sm font-body">Raum Architects Dashboard</p>
          </div>

          {/* OAuth Error */}
          {(oauthError || error) && (
            <div className="mb-6 p-4 rounded-sm bg-red-500/10 border border-red-500/20" data-testid="login-error-message">
              <p className="text-red-400 text-sm font-body">{oauthError || error}</p>
            </div>
          )}

          {/* Google Sign In — Primary */}
          <Button
            data-testid="google-login-btn"
            onClick={googleLogin}
            className="w-full bg-white text-brand-dark hover:bg-gray-100 rounded-sm h-12 font-body font-semibold text-sm flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </Button>

          {/* Access Note */}
          <div className="flex items-start gap-2 mt-4 p-3 rounded-sm bg-white/5">
            <Shield className="w-3.5 h-3.5 text-brand-accent/60 mt-0.5 flex-shrink-0" />
            <p className="text-white/40 text-xs font-body">
              Admin access requires an authorised email or @raum.co.za domain. All Google accounts receive Architect access.
            </p>
          </div>

          {/* Password Fallback — Hidden by default */}
          <div className="mt-6">
            {!showPassword ? (
              <button
                data-testid="show-password-login"
                onClick={() => setShowPassword(true)}
                className="w-full text-center text-white/30 text-xs font-body hover:text-white/50 transition-colors"
              >
                Use password instead
              </button>
            ) : (
              <>
                <Separator className="bg-white/10 mb-6" />
                <form onSubmit={handlePasswordLogin} className="space-y-4">
                  <div className="dark-input">
                    <Label className="text-white/60 text-xs uppercase tracking-wider font-semibold">Email</Label>
                    <Input
                      data-testid="login-email-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="admin@ruam.co.za"
                      className="mt-1.5 bg-white/5 border-white/20 text-white placeholder:text-white/30 rounded-sm h-11 font-body"
                      required
                    />
                  </div>
                  <div className="dark-input">
                    <Label className="text-white/60 text-xs uppercase tracking-wider font-semibold">Password</Label>
                    <Input
                      data-testid="login-password-input"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter password"
                      className="mt-1.5 bg-white/5 border-white/20 text-white placeholder:text-white/30 rounded-sm h-11 font-body"
                      required
                    />
                  </div>
                  <Button
                    data-testid="login-submit-btn"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-brand-accent text-brand-dark hover:bg-brand-accent/90 rounded-sm h-11 font-body font-semibold text-sm uppercase tracking-wider"
                  >
                    {loading ? 'Authenticating...' : 'Sign In'}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
